import { createApi } from '@reduxjs/toolkit/query/react';
import { ApiResponse } from '@/types';

import { customBaseQuery } from '@/lib/baseQuery';
import {
  mapObjectKeysToCamelCase,
  mapObjectKeysToSnakeCase,
} from '@/utils/mapper';

export const propertyAPI = createApi({
  reducerPath: 'propertyAPI',
  baseQuery: customBaseQuery,
  tagTypes: ['Property'],
  endpoints: (builder) => ({
    getPersonalPropertyList: builder.query<
      ApiResponse<any>,
      { property_location?: 'australian' | 'overseas' }
    >({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params?.property_location) {
          queryParams.append('property_location', params.property_location);
        }
        return `api/property/get-property-details/?${queryParams.toString()}`;
      },
      providesTags: ['Property'],
      transformResponse: (response: ApiResponse<any>) => {
        response.data = response.data.map((item: any) => {
          return mapObjectKeysToCamelCase(item);
        });
        return response;
      },
    }),
    getPersonalPropertyById: builder.query<ApiResponse<any>, { id: string }>({
      query: ({ id }) => `api/property/get-property-details/${id}/`,
      transformResponse: (response: ApiResponse<any>) => {
        response.data = mapObjectKeysToCamelCase(response.data);
        return response;
      },
    }),
    postPersonalProperty: builder.mutation<ApiResponse<any>, any>({
      query: (body) => {
        const property_details = mapObjectKeysToSnakeCase(body.propertyDetails);
        const transformedData = mapObjectKeysToSnakeCase(body);
        transformedData.property_details = property_details;
        return {
          url: 'api/property/post-property-details/',
          method: 'POST',
          body: transformedData,
        };
      },
      invalidatesTags: ['Property'],
    }),
    updatePersonalProperty: builder.mutation<
      ApiResponse<any>,
      { data: any; property_id: string }
    >({
      query: ({ data, property_id }) => {
        const property_details = mapObjectKeysToSnakeCase(data.propertyDetails);
        const transformedData = mapObjectKeysToSnakeCase(data);
        transformedData.property_details = property_details;
        return {
          url: `api/property/update-property-details/${property_id}/`,
          method: 'PATCH',
          body: transformedData,
        };
      },
      invalidatesTags: ['Property'],
    }),
    deletePersonalProperty: builder.mutation<ApiResponse<any>, { id: string }>({
      query: ({ id }) => {
        return {
          url: `api/property/delete-property-details/${id}/`,
          method: 'DELETE',
        };
      },
      invalidatesTags: ['Property'],
    }),
    getPropertyWatchlist: builder.query<
      ApiResponse<any>,
      { property_location?: 'australian' | 'overseas' }
    >({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params?.property_location) {
          queryParams.append('property_location', params.property_location);
        }
        return `api/property/get-watchlist-property/?${queryParams.toString()}`;
      },
      providesTags: ['Property'],
      transformResponse: (response: ApiResponse<any>) => {
        response.data = response.data.map((item: any) => {
          return mapObjectKeysToCamelCase(item);
        });
        return response;
      },
    }),
    postPropertyWatchlist: builder.mutation<ApiResponse<any>, any>({
      query: (body) => {
        const property_details = mapObjectKeysToSnakeCase(body.propertyDetails);
        const transformedData = mapObjectKeysToSnakeCase(body);
        transformedData.property_details = property_details;
        return {
          url: 'api/property/post-watchlist-property/',
          method: 'POST',
          body: transformedData,
        };
      },
      invalidatesTags: ['Property'],
    }),
    getPropertyWatchListById: builder.query<ApiResponse<any>, { id: string }>({
      query: ({ id }) => `api/property/get-watchlist-property/${id}/`,
    }),
    updatePropertyWatchlist: builder.mutation<
      ApiResponse<any>,
      { data: any; property_id: string }
    >({
      query: ({ data, property_id }) => {
        const property_details = mapObjectKeysToSnakeCase(data.propertyDetails);
        const transformedData = mapObjectKeysToSnakeCase(data);
        transformedData.property_details = property_details;
        return {
          url: `api/property/update-watchlist-property/${property_id}/`,
          method: 'PUT',
          body: transformedData,
        };
      },
    }),
    deletePropertyWatchlist: builder.mutation<ApiResponse<any>, { id: string }>(
      {
        query: ({ id }) => {
          return {
            url: `api/property/delete-watchlist-property/${id}/`,
            method: 'DELETE',
          };
        },
      },
    ),
  }),
});

export const {
  useGetPersonalPropertyListQuery,
  usePostPersonalPropertyMutation,
  useUpdatePersonalPropertyMutation,
  useGetPropertyWatchlistQuery,
  usePostPropertyWatchlistMutation,
  useGetPersonalPropertyByIdQuery,
  useGetPropertyWatchListByIdQuery,
  useUpdatePropertyWatchlistMutation,
  useDeletePersonalPropertyMutation,
  useDeletePropertyWatchlistMutation,
} = propertyAPI;

export default propertyAPI;
